import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getJobApplications, updateApplicationStatus, scheduleInterview, APPLICATION_STATUS } from '../services/applicationService';
import { getJobById } from '../services/jobService';
import LoadingSpinner from '../components/LoadingSpinner';

const JobApplications = ({ user, userData }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (userData?.userType !== 'employer') {
      navigate('/dashboard');
      return;
    }

    fetchJobAndApplications();
  }, [user, userData, jobId, navigate]);

  const fetchJobAndApplications = async () => {
    setLoading(true);
    try {
      // Fetch job details
      const jobResult = await getJobById(jobId);
      if (jobResult.success) {
        setJob(jobResult.job);
        
        // Verify employer owns this job
        if (jobResult.job.postedBy !== user.uid) {
          navigate('/dashboard');
          return;
        }
      }

      // Fetch applications
      const applicationsResult = await getJobApplications(jobId);
      if (applicationsResult.success) {
        setApplications(applicationsResult.applications);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus, note = '') => {
    setUpdatingStatus(true);
    try {
      const result = await updateApplicationStatus(applicationId, newStatus, {
        note,
        updatedBy: user.uid
      });
      
      if (result.success) {
        setApplications(prev => 
          prev.map(app => 
            app.id === applicationId 
              ? { ...app, status: newStatus, updatedAt: new Date() }
              : app
          )
        );
        setShowStatusModal(false);
        setSelectedApplication(null);
        alert('Application status updated successfully');
      } else {
        alert(result.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [APPLICATION_STATUS.PENDING]: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      [APPLICATION_STATUS.REVIEWING]: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      [APPLICATION_STATUS.SHORTLISTED]: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Shortlisted' },
      [APPLICATION_STATUS.INTERVIEWED]: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Interviewed' },
      [APPLICATION_STATUS.ACCEPTED]: { bg: 'bg-green-100', text: 'text-green-800', label: 'Accepted' },
      [APPLICATION_STATUS.REJECTED]: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      [APPLICATION_STATUS.WITHDRAWN]: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Withdrawn' }
    };

    const config = statusConfig[status] || statusConfig[APPLICATION_STATUS.PENDING];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const getStatusCounts = () => {
    const counts = {
      all: applications.length,
      pending: 0,
      reviewing: 0,
      shortlisted: 0,
      interviewed: 0,
      accepted: 0,
      rejected: 0,
      withdrawn: 0
    };

    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-2">{job.title}</h1>
            <p className="text-xl text-blue-100 mb-6">Applications Management</p>
            <div className="flex items-center justify-center gap-6 text-blue-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{applications.length}</div>
                <div className="text-sm">Total Applications</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{statusCounts.pending + statusCounts.reviewing}</div>
                <div className="text-sm">Pending Review</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{statusCounts.shortlisted + statusCounts.interviewed}</div>
                <div className="text-sm">In Process</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All Applications', count: statusCounts.all },
              { key: APPLICATION_STATUS.PENDING, label: 'Pending', count: statusCounts.pending },
              { key: APPLICATION_STATUS.REVIEWING, label: 'Under Review', count: statusCounts.reviewing },
              { key: APPLICATION_STATUS.SHORTLISTED, label: 'Shortlisted', count: statusCounts.shortlisted },
              { key: APPLICATION_STATUS.INTERVIEWED, label: 'Interviewed', count: statusCounts.interviewed },
              { key: APPLICATION_STATUS.ACCEPTED, label: 'Accepted', count: statusCounts.accepted },
              { key: APPLICATION_STATUS.REJECTED, label: 'Rejected', count: statusCounts.rejected }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  filter === tab.key
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {filter === 'all' ? 'No Applications Yet' : `No ${filter} Applications`}
            </h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? "No one has applied to this job yet."
                : `No applications with ${filter} status.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application) => (
              <div key={application.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{application.applicantName}</h3>
                        <div className="flex items-center gap-4 text-gray-600 text-sm">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {application.applicantEmail}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {application.applicantPhone}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            {application.applicantLocation}
                          </span>
                        </div>
                      </div>
                      {getStatusBadge(application.status)}
                    </div>

                    {/* Skills */}
                    {application.applicantProfile?.skills && application.applicantProfile.skills.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {application.applicantProfile.skills.slice(0, 8).map((skill, index) => (
                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {skill}
                            </span>
                          ))}
                          {application.applicantProfile.skills.length > 8 && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              +{application.applicantProfile.skills.length - 8} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Professional Summary */}
                    {application.applicantProfile?.professionalSummary && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">Professional Summary:</h4>
                        <p className="text-gray-700 text-sm">{application.applicantProfile.professionalSummary}</p>
                      </div>
                    )}

                    {/* Cover Letter */}
                    {application.coverLetter && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">Cover Letter:</h4>
                        <p className="text-gray-700 text-sm">{application.coverLetter}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2" />
                        </svg>
                        <span>Applied: {application.appliedAt?.toLocaleDateString()}</span>
                      </div>
                      
                      {application.applicantProfile?.expectedSalary && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Expected: {application.applicantProfile.expectedSalary.currency} {application.applicantProfile.expectedSalary.min}-{application.applicantProfile.expectedSalary.max}</span>
                        </div>
                      )}
                      
                      {application.applicantProfile?.noticePeriod && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Notice: {application.applicantProfile.noticePeriod}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 min-w-[200px]">
                    {application.resumeURL && (
                      <a
                        href={application.resumeURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-100 text-blue-700 font-medium rounded-lg hover:bg-blue-200 transition-colors text-center"
                      >
                        View Resume
                      </a>
                    )}
                    
                    <button
                      onClick={() => {
                        setSelectedApplication(application);
                        setShowStatusModal(true);
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Update Application Status
            </h3>
            <p className="text-gray-600 mb-4">
              Updating status for: <strong>{selectedApplication.applicantName}</strong>
            </p>
            
            <div className="space-y-3">
              {[
                APPLICATION_STATUS.REVIEWING,
                APPLICATION_STATUS.SHORTLISTED,
                APPLICATION_STATUS.INTERVIEWED,
                APPLICATION_STATUS.ACCEPTED,
                APPLICATION_STATUS.REJECTED
              ].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusUpdate(selectedApplication.id, status)}
                  disabled={updatingStatus}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    selectedApplication.status === status
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-800'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {getStatusBadge(status)}
                </button>
              ))}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedApplication(null);
                }}
                disabled={updatingStatus}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplications;
